import { formatMin, parseWallTimeText } from '@wiztime/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '../../lib/cx';
import { CloseIcon } from '../../ui/icons';
import { TimeField } from '../../ui/TimeField';
import styles from './PlannedTimeField.module.css';

interface PlannedTimeFieldProps {
  /** Stored planned time, wall-clock minutes 0–1439, or null for none. */
  readonly value: number | null;
  /** Name of the check-in item, for the accessible labels. */
  readonly itemName: string;
  /** Accessible label of the input; defaults to the planned-time label of `itemName`. */
  readonly label?: string;
  /** Accessible label of the clear button; defaults to "clear planned time". */
  readonly clearLabel?: string;
  readonly testId?: string;
  /**
   * Fill the width of the parent (the settings table column) instead of the 64px input: the clear
   * button sits inside the input at the right and the input reserves 28px for it at all times, so
   * showing or hiding it never changes the width.
   */
  readonly fill?: boolean;
  /** Reports whether the committed text is currently an unparsable time (for forms that must not submit it). */
  readonly onInvalidChange?: (invalid: boolean) => void;
  /** Enter was pressed in the field, after its text was committed (forms submit from here). */
  readonly onEnter?: () => void;
  /** A valid time or null (cleared) was committed and differs from `value`. */
  readonly onCommit: (next: number | null) => void;
  /**
   * The text as typed, before any commit: the time it parses to, null when empty, undefined when
   * it does not parse (yet). Lets a form react while the user types and save what is in the field.
   */
  readonly onTyping?: (typed: number | null | undefined) => void;
}

/** What `text` would commit as: a time, null for empty, undefined when it does not parse. */
export function typedValueOf(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  return parseWallTimeText(trimmed) ?? undefined;
}

const textOf = (value: number | null): string => (value === null ? '' : formatMin(value));

/**
 * Planned time of a check-in item (2026-10b §8.2): a `TimeField` with the "--:--" placeholder
 * and a "×" that clears it. The format is checked on commit only; an empty text clears the plan,
 * Esc restores the stored value. Used by the card's editing rows and by the settings page.
 */
export function PlannedTimeField({
  value,
  itemName,
  label,
  clearLabel,
  testId,
  fill = false,
  onInvalidChange,
  onEnter,
  onCommit,
  onTyping,
}: PlannedTimeFieldProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(textOf(value));
  const [invalid, setInvalid] = useState(false);
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    // The stored value changed (saved here, undone or synced): show it.
    setSeen(value);
    setText(textOf(value));
    setInvalid(false);
  }

  useEffect(() => {
    onInvalidChange?.(invalid);
  }, [invalid, onInvalidChange]);

  const commit = (): void => {
    const trimmed = text.trim();
    if (trimmed === '') {
      setInvalid(false);
      setText('');
      if (value !== null) onCommit(null);
      return;
    }
    const parsed = parseWallTimeText(trimmed);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setText(formatMin(parsed));
    if (parsed !== value) onCommit(parsed);
  };
  const escape = (): boolean => {
    if (text === textOf(value) && !invalid) return false;
    setText(textOf(value));
    setInvalid(false);
    return true;
  };

  return (
    <span className={cx(styles.wrap, fill && styles.wrapFill)}>
      <span className={cx(styles.field, fill && styles.fieldFill)}>
        <TimeField
          value={text}
          label={label ?? t('checkin.plannedLabel', { name: itemName })}
          placeholder={t('checkin.plannedPlaceholder')}
          invalid={invalid}
          className={fill ? styles.fillTime : undefined}
          testId={testId}
          onChange={(next) => {
            setText(next);
            setInvalid(false);
            onTyping?.(typedValueOf(next));
          }}
          onCommit={commit}
          onEscape={escape}
          onEnterNext={onEnter}
        />
        {value === null ? null : (
          <button
            type="button"
            className={cx(styles.clear, fill && styles.clearFill)}
            aria-label={clearLabel ?? t('checkin.plannedClear')}
            data-testid={testId === undefined ? undefined : `${testId}-clear`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onCommit(null)}
          >
            <CloseIcon />
          </button>
        )}
      </span>
      {invalid ? (
        <span className={styles.error} role="alert">
          {t('checkin.plannedInvalid')}
        </span>
      ) : null}
    </span>
  );
}
